import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../../../../../features/userSlice";
import { DesignTemplatesEndPointLogic } from "../../../../../../logic/AssetManagement/Design/Templates/DesignTemplatesEndPointLogic";
import { formatDate } from "../../../../../../utils/helpers";
import { mapRevisionContent } from "../apiMappers";
import { RevisionContent, TemplateRevision } from "../types";
import { tokens } from "../tokens";
import UserAvatar from "./UserAvatar";
import CodeEditorPanel from "./CodeEditorPanel";
import CodeSkeleton from "./CodeSkeleton";

/**
 * Fixed height for the code area in every state — single view, comparison,
 * and both loading placeholders — so the dialog never resizes as data arrives.
 */
const CODE_VIEW_HEIGHT = 360;

interface Props {
  open: boolean;
  revision: TemplateRevision | null;
  templateSharedUid: string;
  /** What is currently saved — the right-hand side of the comparison. */
  currentContent: RevisionContent;
  currentModifiedAt: string;
  onClose: () => void;
  /**
   * Confirmed "Use This Version" — restores the template to this revision.
   * Resolves true when the update succeeded, so the dialog knows whether to
   * close or stay open for a retry.
   */
  onUseVersion: (
    revision: TemplateRevision,
    content: RevisionContent,
  ) => Promise<boolean>;
}

type CodeTab = "html" | "css";

const metaLabelSx = {
  fontSize: 10,
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  color: tokens.textFaint,
  fontWeight: 700,
};

const currentTagSx = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.03em",
  borderRadius: "4px",
  padding: "2px 6px",
  backgroundColor: tokens.successSoft,
  color: tokens.success,
};

/**
 * Token colours for the diff viewer's own highlighter, matched to the code
 * colours in the design. Applied inline by the library, so nothing leaks into
 * the rest of the app.
 */
const HIGHLIGHT_THEME: Record<string, string> = {
  default: tokens.text,
  tag: "#B0267A",
  selector: "#B0267A",
  "attr-name": "#0E7C86",
  property: "#B7791F",
  "attr-value": "#1B8A5A",
  string: "#1B8A5A",
  comment: tokens.textFaint,
  punctuation: tokens.textMuted,
};

const RevisionDetailModal = ({
  open,
  revision,
  templateSharedUid,
  currentContent,
  currentModifiedAt,
  onClose,
  onUseVersion,
}: Props) => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;

  const [tab, setTab] = useState<CodeTab>("html");
  const [compare, setCompare] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [content, setContent] = useState<RevisionContent | null>(null);
  const [loading, setLoading] = useState(false);
  /** True while the restore PUT is in flight. */
  const [restoring, setRestoring] = useState(false);

  /** Revision bodies are immutable, so fetch each one only once per session. */
  const contentCache = useRef<Record<string, RevisionContent>>({});

  const revisionId = revision?.revisionId ?? "";
  const isCurrent = revision?.isCurrent ?? false;

  const loadContent = useCallback(async () => {
    if (!revisionId || !templateSharedUid || !oauthTokenHeader) return;

    const cached = contentCache.current[revisionId];
    if (cached) {
      setContent(cached);
      return;
    }

    setLoading(true);
    try {
      const raw = await DesignTemplatesEndPointLogic.getDesignTemplateRevision(
        oauthTokenHeader,
        templateSharedUid,
        revisionId,
      );
      if (raw) {
        const mapped = mapRevisionContent(raw);
        contentCache.current[revisionId] = mapped;
        setContent(mapped);
      } else {
        setContent(null);
      }
    } finally {
      setLoading(false);
    }
  }, [oauthTokenHeader, revisionId, templateSharedUid]);

  // Every revision opens on the same default view, and its body is fetched
  // once the dialog is actually shown.
  useEffect(() => {
    if (!open || !revisionId) return;

    setTab("html");
    setCompare(false);
    setConfirming(false);
    setRestoring(false);
    setContent(null);
    loadContent();
  }, [open, revisionId, loadContent]);

  /** Runs the restore and keeps the dialog open if it failed. */
  const handleConfirmRestore = async () => {
    if (!revision || !content || restoring) return;

    setRestoring(true);
    try {
      const succeeded = await onUseVersion(revision, content);
      if (succeeded) setConfirming(false);
    } finally {
      setRestoring(false);
    }
  };

  if (!revision) return null;

  const revisionCode =
    tab === "html" ? (content?.html ?? "") : (content?.css ?? "");
  const currentCode = tab === "html" ? currentContent.html : currentContent.css;

  /**
   * The column headers are rendered here rather than through the library's
   * `leftTitle` / `rightTitle` props: those go into its sticky header row,
   * which measures its own height and clipped the text vertically.
   */
  const diffHeader = (label: string, when: string, color: string) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        padding: "9px 14px",
        minWidth: 0,
      }}
    >
      <Typography
        sx={{ fontSize: 11.5, fontWeight: 700, color, whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          color: tokens.textFaint,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {when}
      </Typography>
    </Box>
  );

  const renderBody = () => {
    if (!loading && !content) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: CODE_VIEW_HEIGHT,
          }}
        >
          <Typography sx={{ fontSize: 13, color: tokens.textFaint }}>
            This revision could not be loaded.
          </Typography>
        </Box>
      );
    }

    if (compare) {
      return (
        <Box
          sx={{
            border: `1px solid ${tokens.border}`,
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              backgroundColor: tokens.surface,
              borderBottom: `1px solid ${tokens.border}`,
              "& > :first-of-type": {
                borderRight: `1px solid ${tokens.border}`,
              },
            }}
          >
            {diffHeader(
              "This revision",
              formatDate(revision.modifiedAt),
              tokens.danger,
            )}
            {diffHeader(
              "Current version",
              `${formatDate(currentModifiedAt)} (current)`,
              tokens.success,
            )}
          </Box>
          {loading || !content ? (
            <CodeSkeleton columns={2} height={CODE_VIEW_HEIGHT} />
          ) : (
            <ReactDiffViewer
              oldValue={revisionCode}
              newValue={currentCode}
              splitView
              compareMethod={DiffMethod.WORDS}
              /* The diff is computed asynchronously; without this the panel is
               briefly empty and the dialog visibly resizes once it lands. */
              loadingElement={() => (
                <CodeSkeleton columns={2} height={CODE_VIEW_HEIGHT} />
              )}
              /* Show the whole file: the design has no collapsed "@@" blocks. */
              showDiffOnly={false}
              /* The summary bar is not part of the design and overlaps the
               column titles when it is sticky. */
              hideSummary
              highlightLanguage={tab}
              highlightTheme={HIGHLIGHT_THEME}
              styles={{
                variables: {
                  light: {
                    diffViewerBackground: tokens.codeBg,
                    gutterBackground: tokens.codeBg,
                    gutterColor: tokens.textFaint,
                    addedBackground: tokens.successSoft,
                    addedColor: tokens.text,
                    removedBackground: tokens.dangerSoft,
                    removedColor: tokens.text,
                    addedGutterBackground: tokens.successSoft,
                    removedGutterBackground: tokens.dangerSoft,
                    wordAddedBackground: "#C7EBD8",
                    wordRemovedBackground: "#F8D2CE",
                    emptyLineBackground: tokens.codeBg,
                  },
                },
                contentText: {
                  fontFamily: tokens.monoFont,
                  fontSize: "11.5px",
                },
                gutter: { fontFamily: tokens.monoFont, fontSize: "11.5px" },
              }}
            />
          )}
        </Box>
      );
    }

    if (loading || !content) {
      return (
        <Box
          sx={{
            border: `1px solid ${tokens.border}`,
            borderRadius: tokens.radius,
            overflow: "hidden",
          }}
        >
          <CodeSkeleton columns={1} height={CODE_VIEW_HEIGHT} />
        </Box>
      );
    }

    return (
      <CodeEditorPanel
        mode={tab}
        value={revisionCode}
        readOnly
        height={`${CODE_VIEW_HEIGHT}px`}
      />
    );
  };

  return (
    <Dialog
      open={open}
      // Cannot be dismissed while a restore is running.
      onClose={() => {
        if (restoring) return;
        onClose();
      }}
      disableEscapeKeyDown={restoring}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: compare ? 1180 : 880,
          maxWidth: "100%",
          maxHeight: "86vh",
          borderRadius: "12px",
          /* Eases the 880 → 1180 change when compare is switched on. */
          transition: "width 200ms ease",
          /* Column layout so the body is the only scrolling region — without
             it the footer (and its "Use This Version" button) is pushed below
             the fold once the diff is tall. */
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: "20px 22px 16px",
          borderBottom: `1px solid ${tokens.border}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{ fontSize: 16.5, fontWeight: 700, color: tokens.text }}
            >
              {revision.name}
            </Typography>
            {isCurrent && (
              <Box component="span" sx={currentTagSx}>
                Latest Revision
              </Box>
            )}
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", gap: 3.5, mt: 1.75, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <UserAvatar email={revision.createdBy} size={26} />
            <Box>
              <Typography sx={metaLabelSx}>Created by</Typography>
              <Typography
                sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.text }}
              >
                {revision.createdBy} · {formatDate(revision.createdAt)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <UserAvatar email={revision.modifiedBy} size={26} />
            <Box>
              <Typography sx={metaLabelSx}>Last modified by</Typography>
              <Typography
                sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.text }}
              >
                {revision.modifiedBy} · {formatDate(revision.modifiedAt)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Tabs + compare toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 22px",
          borderBottom: `1px solid ${tokens.border}`,
          flexShrink: 0,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, value: CodeTab) => setTab(value)}
          sx={{
            minHeight: 0,
            "& .MuiTab-root": {
              minHeight: 0,
              minWidth: 0,
              padding: "12px 6px",
              marginRight: "20px",
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              color: tokens.textFaint,
              "&.Mui-selected": { color: tokens.accent },
            },
            "& .MuiTabs-indicator": { backgroundColor: tokens.accent },
          }}
        >
          <Tab value="html" label="HTML" data-tracker="1088" />
          <Tab value="css" label="CSS" data-tracker="1089" />
        </Tabs>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Switch
            checked={compare}
            disabled={loading || !content}
            onChange={(event) => setCompare(event.target.checked)}
            inputProps={{ "aria-label": "Compare with current version" }}
            data-tracker="1086"
            /* Per the design: solid accent track with a white thumb when on,
               neutral track when off. */
            sx={{
              width: 34,
              height: 20,
              padding: 0,
              display: "flex",
              mr: 1,
              "& .MuiSwitch-switchBase": {
                padding: 0,
                margin: "2px",
                color: "#FFFFFF",
                "&.Mui-checked": {
                  transform: "translateX(14px)",
                  color: "#FFFFFF",
                  "& + .MuiSwitch-track": {
                    backgroundColor: tokens.accent,
                    opacity: 1,
                  },
                },
              },
              "& .MuiSwitch-thumb": {
                width: 16,
                height: 16,
                backgroundColor: "#FFFFFF",
                boxShadow: "0 1px 2px rgba(20, 18, 26, 0.3)",
              },
              "& .MuiSwitch-track": {
                borderRadius: 999,
                backgroundColor: tokens.borderStrong,
                opacity: 1,
              },
            }}
          />
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, color: tokens.textMuted }}
          >
            Compare with current version
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <Box
        sx={{
          padding: "16px 22px",
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
          /* Holds the dialog's height steady across loading, single view and
             comparison, so toggling compare does not make it jump. */
          "& > *": { minHeight: CODE_VIEW_HEIGHT },
        }}
      >
        {renderBody()}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
          padding: "16px 22px",
          borderTop: `1px solid ${tokens.border}`,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 12, color: tokens.textFaint }}>
          Loading a version won't save automatically — you'll still need to
          click Save Changes.
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Button
            onClick={onClose}
            sx={{
              fontSize: 13,
              color: tokens.textMuted,
              textTransform: "none",
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            disabled={loading || !content}
            onClick={() => setConfirming(true)}
            data-tracker="1087"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: tokens.accent,
              "&:hover": { backgroundColor: tokens.accentHover },
            }}
          >
            Use This Version
          </Button>
        </Box>
      </Box>

      {/* Confirmation for replacing the template with this revision. */}
      <Dialog
        open={confirming}
        /* While the request is in flight the dialog cannot be dismissed by a
           backdrop click or Escape. */
        onClose={(_event, reason) => {
          if (restoring) return;
          if (reason === "backdropClick") return;
          setConfirming(false);
        }}
        disableEscapeKeyDown={restoring}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogContent sx={{ padding: "24px 24px 8px" }}>
          <Typography
            sx={{ fontSize: 14, color: tokens.text, lineHeight: 1.6 }}
          >
            This action will replace the current template with this revision.
            Are you sure you want to perform this action?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "8px 20px 20px", gap: 1 }}>
          <Button
            onClick={() => setConfirming(false)}
            disabled={restoring}
            sx={{
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              color: tokens.accent,
              border: `1px solid ${tokens.accent}`,
              borderRadius: tokens.radius,
              px: 2.5,
              "&:hover": { backgroundColor: tokens.accentSoft },
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            disabled={restoring}
            onClick={handleConfirmRestore}
            /* Label stays put and the spinner sits beside it, so the button
               does not resize while the request runs. */
            endIcon={
              restoring ? (
                <CircularProgress size={14} sx={{ color: "#FFFFFF" }} />
              ) : undefined
            }
            sx={{
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: tokens.radius,
              px: 2.5,
              backgroundColor: tokens.accent,
              "&:hover": { backgroundColor: tokens.accentHover },
              "&.Mui-disabled": {
                backgroundColor: tokens.accent,
                color: "#FFFFFF",
                opacity: 0.7,
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default RevisionDetailModal;
